import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

import SvgLoadingAnimation from "../loading";
import Button from "../button/Button";
import IconButton from "../button/IconButton";
import ToolTip from "../tooltip/ToolTip";
import MicroInteraction from "../micro-interaction/MicroInteraction";
// @ts-expect-error - JSX ファイルの型定義なし
import LanguageSwitcher from "../tab/LanguageSwitcher";

import { useGetCreditsQuery } from "../../app/services/credits";
import type { CreditItem } from "../../app/services/credits";
import {
  formatPdfExportError,
  requestStaffCreditPdf,
} from "../../app/services/pdfExports";

import {
  buildCreditPlainText,
  pickJpEn,
  type Language,
} from "./creditTextFormatter";

// ===== styled =====

/** 全画面オーバーレイ（黒背景・白文字） */
const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background: #000;
  color: #fff;
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/**
 * エンドロールを表示するスクロール領域。
 * - スクロールバー非表示。
 * - rAF でゆっくり自動スクロール。手動 wheel / touch は許容（自動スクロールに加算される形）。
 * - クリックで自動スクロールを一時停止/再開（リンククリックは伝播停止で除外）。
 */
const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scroll-behavior: auto;
  -webkit-overflow-scrolling: touch;
  cursor: pointer;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

/**
 * エンドロール本体。上下にスペーサーを置き、開始時は最下端からテキストが流れ込み、
 * 末尾は画面外まで流れていくように見せる。
 */
const EndRoll = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 16px;
`;

/** 上下に挟むスペーサー（viewport 高さ分） */
const Spacer = styled.div`
  width: 100%;
  height: 100dvh;
  flex-shrink: 0;
`;

const ProjectTitle = styled.div.attrs({
  className: "heading-spacing-2448",
})`
  margin-bottom: 64px;
  text-align: center;
  color: #fff;
`;

const SectionBlock = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 56px 0 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.div.attrs({
  className: "heading-spacing-2140",
})`
  text-align: center;
`;

const SectionNote = styled.div.attrs({
  className: "bodytext-13",
})`
  color: #fff;
  text-align: center;
`;

const SubsectionBlock = styled.div.attrs({
  className: "heading-16",
})`
  width: 100%;
  max-width: 720px;
  padding: 4px 0;
  text-align: center;
  color: #fff;
`;

/** エントリー 1 行: 中央で role / name を左右に配置 */
const EntryRow = styled.div`
  width: 100%;
  max-width: 720px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: baseline;
  column-gap: 16px;
  padding: 4px 0;
`;

const EntryRole = styled.div.attrs({
  className: "bodytext-13",
})`
  text-align: right;
  color: #fff;
  word-break: break-word;
`;

const EntryName = styled.div.attrs({
  className: "heading-16",
})`
  text-align: left;
  word-break: break-word;
`;

/**
 * グローバル CSS で `a` 要素自体に font-family / font-size が指定されているため、
 * 親の heading-16 を継承させるには明示的に `font: inherit` を指定する。
 */
const NameLink = styled.a`
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  font: inherit;

  &:hover {
    color: var(--soremo-blue);
  }
`;

const SpaceBlock = styled.div<{ $lines: number }>`
  width: 100%;
  height: ${(p) => Math.max(1, p.$lines) * 24}px;
`;

const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
`;

/** 左上の「戻る」ボタン領域。黒背景上で白基調＋ホバー時のみ青に上書き。 */
const TopLeftBack = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;

  & .material-symbols-rounded {
    color: #fff;
    transition: color 0.2s;
  }
  &:hover .material-symbols-rounded {
    color: var(--soremo-blue);
  }
`;

/** 固定フッター */
const FixedFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 12px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
`;

/** フッター左に置く言語切替。LanguageSwitcher の `u-w100` 幅を打ち消し、白基調に上書き。 */
const FooterLanguage = styled.div`
  display: flex;
  align-items: center;

  & > div {
    width: auto;
  }

  & .u-text-blue {
    color: #fff;
    transition: color 0.2s;
  }
  &:hover .u-text-blue {
    color: var(--soremo-blue);
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/**
 * 黒背景上の outlined ボタン用カラーオーバーライド。
 * 通常: 白文字＋白枠／ホバー: 青文字＋青枠（既存 Button の透過背景・シャドウは維持）
 */
const WhiteOutlineButtonWrap = styled.div`
  & button:not(:disabled) {
    color: #fff;
    border-color: #fff;
  }
  & button:not(:disabled) .material-symbols-rounded {
    color: inherit;
  }
  & button:not(:disabled):hover,
  & button:not(:disabled):focus {
    color: var(--soremo-blue);
    border-color: var(--soremo-blue);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/**
 * PDF 生成中のボタンに、回転する progress_activity アイコンを適用するためのラッパ。
 * Material Symbols アイコンを 1 周回転させ、視覚的に処理中であることを示す。
 */
const SpinningIconWrap = styled.div`
  & button .material-symbols-rounded {
    animation: ${spin} 1s linear infinite;
  }
`;

/**
 * PDF 生成失敗時にフッター上部へ表示する短いエラーバナー。
 * 黒背景の上で読めるよう、半透明の赤系背景＋白文字で表示する。
 */
const PdfErrorBanner = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 64px;
  padding: 8px 12px;
  border-radius: 4px;
  background: rgba(220, 38, 38, 0.92);
  color: #fff;
  font-size: 13px;
  text-align: center;
  white-space: pre-line;
  z-index: 3;
`;

// ===== helpers (component 外) =====

/** rAF による自動スクロール速度（px / 秒） */
const AUTO_SCROLL_SPEED_PX_PER_SEC = 35;

/** entry の表示名のリンク先を解決する。link_url を優先、無ければ linked_user.profile_url。 */
const resolveEntryLink = (item: CreditItem): string | null => {
  const e = item.entry;
  if (!e) return null;
  if (e.link_url && e.link_url.trim() !== "") return e.link_url;
  const profile = e.linked_user?.profile_url;
  if (profile && profile.trim() !== "") return profile;
  return null;
};

/**
 * PDF 保存時のファイル名を生成する。
 * OS が弾く文字（`/ \ : * ? " < > |`）と前後の空白・ドットを除去/置換する。
 */
const buildPdfFilename = (projectTitle?: string): string => {
  const base = (projectTitle ?? "").trim();
  if (!base) return "staff-credit.pdf";
  const sanitized = base
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .replace(/^[.\s]+|[.\s]+$/g, "")
    .slice(0, 120);
  return `${sanitized || "staff-credit"}.pdf`;
};

/** Blob を <a download> でブラウザに保存させる。 */
const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
};

// ===== component =====

export interface CreditViewModalProps {
  projectId: string;
  projectTitle?: string;
  onClose: () => void;
}

const CreditViewModal: React.FC<CreditViewModalProps> = ({
  projectId,
  projectTitle,
  onClose,
}) => {
  const { data, isLoading, error } = useGetCreditsQuery({ projectId });
  const [language, setLanguage] = useState<Language>("JP");
  const [isPaused, setIsPaused] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<
    "idle" | "generating" | "success" | "error"
  >("idle");
  const [pdfErrorMsg, setPdfErrorMsg] = useState<string | null>(null);
  const pdfErrorTimerRef = useRef<number | null>(null);

  const items = useMemo<CreditItem[]>(
    () => (data?.data?.credits ?? []).slice().sort((a, b) => a.order - b.order),
    [data],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const reachedEndRef = useRef(false);
  const isPausedRef = useRef(false);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // ===== 自動スクロール =====
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isLoading) return;

    const tick = (ts: number) => {
      if (lastTsRef.current === null) {
        lastTsRef.current = ts;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const dt = ts - lastTsRef.current;
      lastTsRef.current = ts;

      if (!reachedEndRef.current && !isPausedRef.current) {
        const max = el.scrollHeight - el.clientHeight;
        if (max <= 0) {
          reachedEndRef.current = true;
        } else {
          const next = el.scrollTop + (AUTO_SCROLL_SPEED_PX_PER_SEC * dt) / 1000;
          if (next >= max) {
            el.scrollTop = max;
            reachedEndRef.current = true;
          } else {
            el.scrollTop = next;
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [isLoading, items.length]);

  // ===== クリックでスクロール停止/再開（リンククリックは除外） =====
  const handleScrollAreaClick = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  // ===== Esc で閉じる =====
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ===== コピー =====
  const handleCopy = useCallback(() => {
    const text = buildCreditPlainText({
      items,
      language,
      projectTitle,
    });
    navigator.clipboard.writeText(text).catch((e) => {
      console.warn("[CreditViewModal] clipboard.writeText failed:", e);
    });
  }, [items, language, projectTitle]);

  // ===== PDF 書き出し =====
  // generating 中は再クリックを弾き、結果に応じて status を遷移させる。
  // エラー表示は 6 秒後に自動で消す（再操作時は明示的にクリア）。
  const handleExportPdf = useCallback(async () => {
    if (pdfStatus === "generating") return;
    if (pdfErrorTimerRef.current !== null) {
      window.clearTimeout(pdfErrorTimerRef.current);
      pdfErrorTimerRef.current = null;
    }
    setPdfErrorMsg(null);
    setPdfStatus("generating");
    try {
      const { blob, filename } = await requestStaffCreditPdf({
        projectId,
        language: language === "EN" ? "en" : "jp",
      });
      triggerBlobDownload(blob, filename || buildPdfFilename(projectTitle));
      setPdfStatus("success");
      window.setTimeout(() => {
        setPdfStatus((prev) => (prev === "success" ? "idle" : prev));
      }, 1500);
    } catch (e) {
      console.error("[CreditViewModal] PDF export failed:", e);
      setPdfErrorMsg(formatPdfExportError(e));
      setPdfStatus("error");
      pdfErrorTimerRef.current = window.setTimeout(() => {
        setPdfStatus((prev) => (prev === "error" ? "idle" : prev));
        setPdfErrorMsg(null);
        pdfErrorTimerRef.current = null;
      }, 6000);
    }
  }, [language, pdfStatus, projectId, projectTitle]);

  useEffect(() => {
    return () => {
      if (pdfErrorTimerRef.current !== null) {
        window.clearTimeout(pdfErrorTimerRef.current);
        pdfErrorTimerRef.current = null;
      }
    };
  }, []);

  // ===== 描画 =====
  const renderItem = (item: CreditItem) => {
    switch (item.item_type) {
      case "section": {
        const s = item.section;
        const title = pickJpEn(s?.title, s?.title_en, language);
        const note = pickJpEn(s?.note, s?.note_en, language);
        return (
          <SectionBlock key={item.item_id}>
            {title && <SectionTitle>{title}</SectionTitle>}
            {note && <SectionNote>{note}</SectionNote>}
          </SectionBlock>
        );
      }
      case "subsection": {
        const s = item.subsection;
        const title = pickJpEn(s?.title, s?.title_en, language);
        if (!title) return null;
        return <SubsectionBlock key={item.item_id}>{title}</SubsectionBlock>;
      }
      case "entry": {
        const e = item.entry;
        const role = pickJpEn(e?.role, e?.role_en, language);
        const name = pickJpEn(e?.display_name, e?.display_name_en, language);
        const link = resolveEntryLink(item);
        return (
          <EntryRow key={item.item_id}>
            <EntryRole>{role}</EntryRole>
            <EntryName>
              {link ? (
                <NameLink
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  {name}
                </NameLink>
              ) : (
                name
              )}
            </EntryName>
          </EntryRow>
        );
      }
      case "space": {
        const lines = item.space?.lines ?? 1;
        return <SpaceBlock key={item.item_id} $lines={lines} />;
      }
      // track / image は現状エンドロール表示を未対応（今後拡張予定）
      default:
        return null;
    }
  };

  return createPortal(
    <FullscreenOverlay role="dialog" aria-modal="true">
      <TopLeftBack>
        <ToolTip description="戻る" placement="right" theme="on-dark">
          <IconButton onClick={onClose} icon="chevron_left" />
        </ToolTip>
      </TopLeftBack>

      <ScrollArea ref={scrollRef} onClick={handleScrollAreaClick}>
        {isLoading ? (
          <EmptyState>
            <SvgLoadingAnimation />
          </EmptyState>
        ) : error ? (
          <EmptyState>
            {(error as { status?: number }).status === 403
              ? "このプロジェクトのクレジットを表示する権限がありません。"
              : "クレジットの読み込みに失敗しました。"}
          </EmptyState>
        ) : items.length === 0 ? (
          <EmptyState>クレジットはまだありません。</EmptyState>
        ) : (
          <EndRoll>
            <Spacer aria-hidden />
            {projectTitle && <ProjectTitle>{projectTitle}</ProjectTitle>}
            {items.map(renderItem)}
            <Spacer aria-hidden />
          </EndRoll>
        )}
      </ScrollArea>

      <FixedFooter>
        <FooterLanguage>
          <LanguageSwitcher onLanguageChange={setLanguage} />
        </FooterLanguage>

        <FooterRight>
          <WhiteOutlineButtonWrap>
            <MicroInteraction
              text="コピーしました！"
              placement="top"
              animationType="bounce"
              icon="check"
              trigger={
                <Button
                  size="small"
                  theme="primary"
                  variant="outlined"
                  leftIcon="content_copy"
                  onClick={handleCopy}
                >
                  テキストをコピー
                </Button>
              }
            />
          </WhiteOutlineButtonWrap>

          {pdfStatus === "generating" ? (
            <SpinningIconWrap>
              <Button
                size="small"
                theme="primary"
                variant="outlined"
                leftIcon="progress_activity"
                disabled
              >
                PDF生成中…
              </Button>
            </SpinningIconWrap>
          ) : pdfStatus === "success" ? (
            <WhiteOutlineButtonWrap>
              <Button
                size="small"
                theme="primary"
                variant="outlined"
                leftIcon="check"
                onClick={handleExportPdf}
              >
                ダウンロードしました
              </Button>
            </WhiteOutlineButtonWrap>
          ) : (
            <WhiteOutlineButtonWrap>
              <Button
                size="small"
                theme="primary"
                variant="outlined"
                leftIcon="picture_as_pdf"
                onClick={handleExportPdf}
              >
                PDF書き出し
              </Button>
            </WhiteOutlineButtonWrap>
          )}
        </FooterRight>
      </FixedFooter>

      {pdfStatus === "error" && pdfErrorMsg && (
        <PdfErrorBanner role="alert">{pdfErrorMsg}</PdfErrorBanner>
      )}
    </FullscreenOverlay>,
    document.body,
  );
};

export default CreditViewModal;
